export type AlertSourceId = 'teseras' | 'production';

export type AlertWindow = '24h' | '7d' | '30d';

export type AlertEnvironment =
  | 'test'
  | 'staging'
  | 'production'
  | 'development'
  | 'shared-non-production'
  | 'unattributed';

/**
 * The environment a Portal deployment shows alerts for. Each Portal reads only
 * its own Azure subscription and only its own environment's alerts.
 */
export type AlertScope = 'test' | 'staging' | 'production' | 'development';

/** The single Azure subscription a Portal of the given scope reads. */
export function alertSourceForScope(scope: AlertScope): AlertSourceId {
  return scope === 'production' ? 'production' : 'teseras';
}

/**
 * Whether an alert attributed to `environment` belongs on a Portal of `scope`.
 * Non-production Portals also show alerts from shared non-production
 * infrastructure (for example the global Front Door), labelled as shared.
 * Unattributed alerts are never shown on any Portal.
 */
export function isAlertInScope(
  scope: AlertScope,
  environment: AlertEnvironment,
): boolean {
  if (environment === scope) return true;
  return scope !== 'production' && environment === 'shared-non-production';
}

export type AlertCondition = 'Fired' | 'Resolved';

export type AlertQuery = { window: AlertWindow } | { cursor: string };

export type AlertCollectionError =
  | 'configuration_error'
  | 'unauthorized'
  | 'rate_limited'
  | 'timeout'
  | 'invalid_response'
  | 'query_failed';

/** A traceability value Azure recorded on the fired alert instance. */
export interface AlertDetail {
  label: 'Host' | 'WAF rule';
  value: string;
}

export interface CentralizedAlert {
  id: string;
  sourceId: AlertSourceId;
  ruleName: string;
  /** The alert rule's own description of what it detects, when it has one. */
  description: string | null;
  severity: 'Sev0' | 'Sev1' | 'Sev2' | 'Sev3' | 'Sev4';
  monitorCondition: AlertCondition;
  alertState: 'New' | 'Acknowledged' | 'Closed';
  environment: AlertEnvironment;
  /** Allowlisted alert dimensions (never client IPs). */
  details: AlertDetail[];
  firedAt: string;
  lastModifiedAt: string;
  targetResourceId: string;
  targetResourceName: string;
  alertUrl: string;
  resourceUrl: string;
  logsUrl: string | null;
  /** Log Analytics view of the exact rows that fired a log-query alert. */
  matchingLogsUrl: string | null;
}

export interface AlertSourcePage {
  sourceId: AlertSourceId;
  status: 'success' | 'error';
  error: { code: AlertCollectionError; message: string } | null;
  queriedAt: string;
  lastSuccessfulAt: string | null;
  pageKey: string | null;
  items: CentralizedAlert[];
  /**
   * Alerts on this page whose resource is not in the environment mapping.
   * They are not shown on any Portal until their resource is mapped.
   */
  unattributedCount: number;
  exhausted: boolean;
}

export interface CentralizedAlertsResponse {
  schemaVersion: 2;
  scope: AlertScope;
  window: AlertWindow;
  from: string;
  to: string;
  expiresAt: string;
  /** Exactly one page, from the Portal's own subscription. */
  sources: AlertSourcePage[];
  nextCursor: string | null;
}
